import React from 'react';
import qs from 'qs';
import { withRouter, WithRouterProps } from 'react-router';

import ModalDialog from './modal_dialog';
import FormGroupWithInput from './form_group_with_input';
import FormGroupWithSelect from './form_group_with_select';
import { withPrefix } from '../i18n_helpers';
import { useUser } from '../user_hooks';
import { experimentInt } from '../user_context_helpers';
import experiments from '../experiments';

interface ExternalProps {
  isModalOpen: boolean;
  setModal: Function;
  propsPostalCode?: string;
  propsDistance?: string;
  includeRadiusDropdown?: boolean;
  shouldApplyPostalCodeQueryParam?: boolean; // should we apply `postal_code=XXXXX` to the post-submit URL
  shouldApplyDistanceQueryParam?: boolean; // should we apply `distance=X` to the post-submit URL
  additionalQueryParams?: Record<string, any>; // any additional query params to append to the post-submit URL
}

const t = withPrefix('commons.proximityFilter');

export const POSTAL_CODE_PARAM = 'postal_code';
export const DISTANCE_PARAM = 'distance';

type IProps = ExternalProps & WithRouterProps;

const MILES_5 = '5';
const MILES_25 = '25';
const MILES_50 = '50';
const MILES_100 = '100';
const MILES_250 = '250';
const MILES_500 = '500';

const DISTANCE_OPTIONS = [
  MILES_5,
  MILES_25,
  MILES_50,
  MILES_100,
  MILES_250,
  MILES_500,
];

export function DefaultDistance() {
  const user = useUser();
  const shopLocalExpBucket = experimentInt(user, experiments.SHOP_LOCAL_V2);

  switch (shopLocalExpBucket) {
    case 1:
      return MILES_25;
    case 2:
      return MILES_50;
    case 3:
      return MILES_100;
    default:
      return MILES_50;
  }
}

function buildDistanceValue(distance) {
  return {
    text: t('modalDistanceMiles', { distance }),
    value: distance,
  };
}

function getPostSubmitUrl({
  location,
  postalCode,
  distance,
  applyPostalCodeQueryParam,
  applyDistanceQueryParam,
  additionalQueryParams,
}) {
  if (!applyPostalCodeQueryParam && !applyDistanceQueryParam && !additionalQueryParams) { return null; }
  const { pathname, query: currentQuery } = location || {};
  if (!pathname) { return null; }

  const updatedQueryParams = {
    ...currentQuery,
    ...(applyPostalCodeQueryParam && { [POSTAL_CODE_PARAM]: postalCode }),
    ...(applyDistanceQueryParam && { [DISTANCE_PARAM]: distance }),
    ...additionalQueryParams,
  };

  return `${pathname}?${qs.stringify(updatedQueryParams)}`;
}

export function ProximityModal({
  propsPostalCode,
  propsDistance,
  location,
  isModalOpen,
  setModal,
  includeRadiusDropdown = false,
  shouldApplyPostalCodeQueryParam = false,
  shouldApplyDistanceQueryParam = false,
  additionalQueryParams,
}: IProps) {
  const user = useUser();
  const defaultPostalCodeValue = propsPostalCode || user.postalCode || '';
  const defaultDistanceValue = propsDistance || DefaultDistance();

  const [postalCode, setPostalCode] = React.useState(defaultPostalCodeValue);
  const [distance, setDistance] = React.useState(defaultDistanceValue);

  return (
    <ModalDialog
      headerTitle={t('adjustLocation')}
      isOpen={isModalOpen}
      onRequestClose={() => setModal(false)}
      saveButtonText={t('update')}
      formAction="/users/proximity_settings"
      isDisabled={!postalCode}
      useCsrfForm
    >
      <div className="g-container">
        <div className="g-col-6">
          <FormGroupWithInput
            inputName={POSTAL_CODE_PARAM}
            label={t('zipCode')}
            updateField={(e) => { setPostalCode(e[POSTAL_CODE_PARAM]); }}
            value={postalCode}
            numberOnly
            minLength={5}
            maxLength={5}
          />
        </div>

        {includeRadiusDropdown && (
          <div className="g-col-6">
            <FormGroupWithSelect
              inputName={DISTANCE_PARAM}
              label={t('radius')}
              updateField={(e) => setDistance(e.distance)}
              value={distance}
              options={DISTANCE_OPTIONS.map((v) => buildDistanceValue(v))}
            />
          </div>
        )}

        <input
          name="redirect_to"
          value={getPostSubmitUrl({
            location,
            postalCode,
            distance,
            applyPostalCodeQueryParam: (shouldApplyPostalCodeQueryParam && postalCode),
            applyDistanceQueryParam: (shouldApplyDistanceQueryParam && includeRadiusDropdown && distance),
            additionalQueryParams,
          })}
          type="hidden"
        />
      </div>
    </ModalDialog>
  );
}

export default withRouter(ProximityModal);
